import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send, 
  MessageCircle, 
  Camera, 
  Globe, 
  Heart, 
  Star, 
  Award, 
  Zap, 
  Shield, 
  Users, 
  Briefcase, 
  Coffee,
  ArrowRight,
  ExternalLink,
  Code,
  Film
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SITE_CONTACT, getSiteLocation } from '../constants/siteContact';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, language } = useLanguage();

  const services = [
    { name: t('services.web.title'), path: '/services', icon: <Globe className="w-5 h-5" /> },
    { name: t('services.mobile.title'), path: '/services', icon: <Phone className="w-5 h-5" /> },
    { name: t('services.cloud.title'), path: '/services', icon: <Shield className="w-5 h-5" /> },
    { name: t('services.ai.title'), path: '/services', icon: <Zap className="w-5 h-5" /> }
  ];

  const company = [
    { name: t('footer.about'), path: '/about', icon: <Users className="w-5 h-5" /> },
    { name: t('footer.team'), path: '/team', icon: <Award className="w-5 h-5" /> },
    { name: t('footer.careers'), path: '/careers', icon: <Briefcase className="w-5 h-5" /> },
    { name: t('footer.blog'), path: '/blog', icon: <Coffee className="w-5 h-5" /> }
  ];

  const quickLinks = [
    { name: t('nav.home'), path: '/', icon: <Star className="w-4 h-4" /> },
    { name: t('footer.services'), path: '/services', icon: <Heart className="w-4 h-4" /> },
    { name: t('footer.portfolio'), path: '/portfolio', icon: <Award className="w-4 h-4" /> },
    { name: t('footer.contact'), path: '/contact', icon: <Mail className="w-4 h-4" /> }
  ];

  const socialLinks = [
    { icon: <MessageCircle className="w-5 h-5" />, href: 'https://facebook.com/devalpha', label: 'Facebook', color: '#1877f2' },
    { icon: <Send className="w-5 h-5" />, href: 'https://twitter.com/devalpha', label: 'Twitter', color: '#1da1f2' },
    { icon: <ExternalLink className="w-5 h-5" />, href: 'https://linkedin.com/company/devalpha', label: 'LinkedIn', color: '#0077b5' },
    { icon: <Code className="w-5 h-5" />, href: 'https://github.com/devalpha', label: 'GitHub', color: '#333333' },
    { icon: <Camera className="w-5 h-5" />, href: 'https://instagram.com/devalpha', label: 'Instagram', color: '#e4405f' },
    { icon: <Film className="w-5 h-5" />, href: 'https://youtube.com/devalpha', label: 'YouTube', color: '#ff0000' }
  ];

  const legalLinks = [
    { name: t('footer.privacy'), path: '/privacy' },
    { name: t('footer.terms'), path: '/terms' },
    { name: t('footer.cookies'), path: '/cookies' }
  ];

  return (
    <footer style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      color: 'white',
      padding: '80px 20px 40px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
        opacity: 0.5
      }} />

      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        left: '-100px',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(100px)',
        animation: 'float 20s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-80px',
        right: '-80px',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 25s ease-in-out infinite reverse'
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
        textAlign: 'center'
      }}>
        {/* Main Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '60px',
          marginBottom: '60px'
        }}>
          {/* Logo et Description */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '30px',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <img
                src="/favicon.svg"
                alt="DevAlpha"
                width={36}
                height={36}
                style={{ borderRadius: '10px', display: 'block' }}
              />
              <span style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #1e293b, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
              }}>
                DevAlpha
              </span>
            </div>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
              lineHeight: '1.6',
              maxWidth: '600px',
              textAlign: 'center'
            }}>
              {t('footer.tagline')}
            </p>
          </div>

          {/* Contact Information */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '40px',
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            <a
              href={`tel:${SITE_CONTACT.phone}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1rem',
                textDecoration: 'none',
              }}
            >
              <Phone className="w-5 h-5" />
              <span>{SITE_CONTACT.phoneDisplay}</span>
            </a>
            <a
              href={`mailto:${SITE_CONTACT.email}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1rem',
                textDecoration: 'none',
              }}
            >
              <Mail className="w-5 h-5" />
              <span>{SITE_CONTACT.email}</span>
            </a>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '1rem'
            }}>
              <MapPin className="w-5 h-5" />
              <span>{getSiteLocation(language)}</span>
            </div>
          </div>

          {/* Links Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '60px',
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            {/* Services */}
            <div>
              <h4 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                marginBottom: '25px',
                color: 'white',
                textAlign: 'center'
              }}>
{t('footer.services')}
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                textAlign: 'left'
              }}>
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      padding: '8px 0'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#8b5cf6';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    {service.icon}
                    <span>{service.name}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              ))}
            </ul>
            </div>

            {/* Company */}
            <div>
              <h4 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                marginBottom: '25px',
                color: 'white',
                textAlign: 'center'
              }}>
{t('footer.company')}
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                textAlign: 'left'
              }}>
              {company.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      padding: '8px 0'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#8b5cf6';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              ))}
            </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                marginBottom: '25px',
                color: 'white',
                textAlign: 'center'
              }}>
{t('footer.quickLinks')}
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                textAlign: 'left'
              }}>
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      padding: '8px 0'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#8b5cf6';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    {link.icon}
                    <span>{link.name}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              ))}
            </ul>
            </div>
          </div>

          {/* Social Media & Newsletter */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h4 style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                marginBottom: '25px',
                color: 'white'
              }}>
{t('footer.social')}
              </h4>
              <div style={{
                display: 'flex',
                gap: '15px',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = social.color;
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {social.icon}
                  <span>{social.label}</span>
                </a>
              ))}
            </div>
            </div>

            {/* Newsletter */}
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              padding: '25px',
              borderRadius: '20px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              backdropFilter: 'blur(10px)',
              maxWidth: '400px'
            }}>
              <h5 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '15px',
                color: 'white'
              }}>
{t('footer.newsletter')}
              </h5>
              <div style={{
                display: 'flex',
                gap: '10px'
              }}>
                <input
                  type="email"
                  placeholder={t('footer.newsletter.placeholder')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
                <button
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6, #3b82f6)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Send className="w-4 h-4" />
{t('footer.newsletter.button')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 0 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          flexWrap: 'wrap',
          gap: '30px'
        }}>
          <div style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            © {currentYear} DevAlpha. {t('footer.rights')}.
          </div>
          
          <div style={{
            display: 'flex',
            gap: '30px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {legalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease',
                  padding: '4px 0'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#8b5cf6';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                }}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
